import styled from '@emotion/styled';
import {
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  mediaMax,
} from 'presentations/views/styles';
import {
  primary,
  black,
  white,
  gray02,
  gray03,
  gray04,
  gray06,
  blue02,
  secondary,
} from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import ResourceShareList from 'presentations/views/components/organisms/ResourceShareList';
import { ProductType } from 'domains/models/blog_post/productType';
import Head from 'next/head';
import ResourceEmbedContent from 'presentations/views/components/molecules/ResourceEmbedContent';
import { BlogItem } from 'presentations/api/schema/contents';

export interface Props {
  blog: BlogItem;
  productType: ProductType;
  isShowAd: boolean;
  shareTitle: string;
  shareUrl: string;
}

export default function Component(props: Props) {
  return (
    <ContentWrapper>
      <Head>
        {props.blog.thumbnailUrl && (
          <link rel="preload" href={props.blog.thumbnailUrl} as="image" />
        )}
      </Head>
      <ContentContainer>
        <div id="js-resource-body">
          <BodyContainer
            dangerouslySetInnerHTML={{ __html: props.blog.body }}
          />
          {props.isShowAd && (
            <>
              <ResourceEmbedContent
                productType={props.productType}
                customers={[]}
              />
            </>
          )}
        </div>
        {/* <TagsContainer>
          <ResourceTagList tags={props.blog.tags} />
        </TagsContainer> */}
      </ContentContainer>
      {!props.blog.isUnlisted && (
        <SideShareIconContainer>
          <SideShareIconStickyWrapper>
            <SideShareIconSticky>
              <ResourceShareList
                shareTitle={props.shareTitle}
                shareUrl={props.shareUrl}
              />
            </SideShareIconSticky>
          </SideShareIconStickyWrapper>
        </SideShareIconContainer>
      )}
    </ContentWrapper>
  );
}

const ContentWrapper = styled.div`
  position: relative;
`;

const ContentContainer = styled.div``;

export const BodyContainer = styled.div`
  margin: ${spacing(48)} 0 ${spacing(40)};
  letter-spacing: 0.05em;
  font-size: ${fontSize(16)};
  line-height: 1.8;
  word-break: break-all;

  ${mediaMax.tablet} {
    margin: ${spacing(20)} 0 ${spacing(20)} 0;
  }

  h2 {
    margin: 3rem 0 ${spacing(32)} 0;
    font-size: ${fontSize(24)};
    line-height: 1.5;
    padding-bottom: ${spacing(24)};
    border-bottom: 1px solid ${primary};

    ${mediaMax.tablet} {
      margin: 3rem 0 2rem 0;
      font-size: ${fontSize(20)};
    }
  }

  h3 {
    margin: 2rem 0 ${spacing(16)} 0;
    font-size: ${fontSize(20)};
    line-height: 1.5;
    background: ${gray04};
    padding: ${spacing(16)} ${spacing(24)};
    border-radius: ${borderRadius(6)};

    ${mediaMax.tablet} {
      margin: 2rem 0 1rem 0;
      font-size: ${fontSize(18)};
    }
  }

  h4 {
    font-size: ${fontSize(16)};
    margin: 2rem 0 ${spacing(16)} 0;
    line-height: 1.5;
    border-left: 3px solid ${primary};
    padding-left: ${spacing(12)};
    ${mediaMax.tablet} {
      margin: 2rem 0 1rem 0;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: ${borderRadius()};
    /* object-fit: cover; */
    margin-bottom: 1.5rem;
    border: 1px solid ${gray04};
  }

  p {
    margin: 0 0 ${spacing(24)} 0;
    line-height: 180%;

    ${mediaMax.tablet} {
      margin: 0 0 ${spacing(20)} 0;
    }

    :last-of-type {
      margin: 0 0 ${spacing(32)} 0;

      ${mediaMax.tablet} {
        margin: 0 0 ${spacing(24)} 0;
      }
    }
  }

  & > a,
  .IconArrow {
    display: flex;
    align-items: center;
    margin: ${spacing(12)} 0;
    color: ${black};
    text-decoration: none;
    &:before {
      font-family: 'Material Icons';
      font-size: ${fontSize(24)};
      content: 'chevron_right';
      color: ${primary};
      vertical-align: middle;
    }
    &:hover {
      color: ${primary};
    }
  }

  & > * a {
    text-decoration: underline;
  }

  blockquote {
    padding: ${spacing(40)} ${spacing(56)} ${spacing(32)} ${spacing(64)};
    margin: ${spacing(40)} 0;
    background-color: ${gray06};
    background-image: url(${imagePath('pages/resource/blockquote.svg')});
    background-repeat: no-repeat;
    background-position: ${spacing(24)} ${spacing(24)};
    border-radius: ${borderRadius()};
    font-size: ${fontSize(14)};

    p {
      :last-of-type {
        margin: 0;
      }
    }

    ${mediaMax.tablet} {
      padding: ${spacing(32)} ${spacing(40)};
      margin: ${spacing(20)} 0;
      background-position: ${spacing(16)} ${spacing(16)};
      background-size: 16px;
      line-height: 1.6;
    }
  }

  ol {
    margin-bottom: ${spacing(32)};
    padding-left: 2em;
    list-style-type: decimal;

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(20)};
    }
  }

  ol > li {
    margin-bottom: ${spacing(16)};

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(12)};
    }

    :last-of-type {
      margin-bottom: 0;
    }
  }

  ul {
    margin-bottom: ${spacing(32)};

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(20)};
    }
  }

  ul > li {
    position: relative;
    margin-bottom: ${spacing(16)};
    padding-left: 2em;

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(12)};
    }

    :last-of-type {
      margin-bottom: 0;
    }

    &:before {
      position: absolute;
      top: 0;
      left: 8px;

      font-family: 'Material Icons';
      font-size: ${fontSize(12)};
      content: 'remove';
      color: ${black};
      vertical-align: middle;
    }
  }
  .TableBox {
    padding: 0;
    border-radius: 0;
    ${mediaMax.tablet} {
      overflow: auto;
    }
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  pre {
    background: ${black};
    color: ${white};
    padding: ${spacing(16)};
    overflow: scroll;
    margin-bottom: ${spacing(16)};
  }
  th {
    padding: ${spacing(8)};
    border: 1px solid ${gray03};
    font-weight: ${fontWeight('bold')};
    vertical-align: center;
    min-width: 130px;
    background-color: ${gray06};
  }
  td {
    padding: ${spacing(8)};
    border: 1px solid ${gray03};
    min-width: 130px;
    background-color: ${white};
  }

  div {
    margin-bottom: ${spacing(40)};
    padding: ${spacing(40)};
    background-color: ${blue02};
    border-radius: ${borderRadius()};
    line-height: 180%;

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(20)};
      padding: ${spacing(24)};
    }
  }

  .CpLeadText {
    font-size: ${fontSize(16)};
    font-weight: ${fontWeight('bold')};
    ${mediaMax.tablet} {
      font-size: ${fontSize(16)};
    }
  }
  .AccentText {
    color: ${secondary};
  }
  .PromotionBox {
    border-radius: ${borderRadius(16)};
    text-align: center;
  }
  .PromotionCode {
    font-size: ${fontSize(12)};
    margin-bottom: ${spacing(24)};
    color: ${gray02};
    span {
      display: block;
      margin-top: ${spacing(8)};
      font-size: ${fontSize(24)};
      font-weight: ${fontWeight('bold')};
      color: ${black};
    }
  }
  .Btn {
    padding: ${spacing(16)} ${spacing(32)};
    margin: 0 0 ${spacing(8)};
    background: ${primary};
    color: ${white};
    display: inline-block;
    font-size: ${fontSize(14)};
    font-weight: ${fontWeight('bold')};
    border-radius: 50px;
    text-decoration: none;
  }
  .MemberRegistration {
    display: block;
    a {
      justify-content: center;
    }
  }
  .jetpack-video-wrapper {
    padding: 0;
    iframe {
      width: 100%;
      ${mediaMax.smartphone} {
        height: 100%;
      }
    }
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-flow: row;
  margin: 0 0 ${spacing(64)} 0;
  padding: ${spacing(16)} 0;

  border-top: 1px solid ${gray04};
  border-bottom: 1px solid ${gray04};

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(28)} 0;
    padding: ${spacing(12)} 0;
  }
`;

const SideShareIconContainer = styled.div`
  position: absolute;
  top: 20px;
  left: -64px;
  height: 100%;

  ${mediaMax.tablet} {
    display: none;
  }
`;

const SideShareIconStickyWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const SideShareIconSticky = styled.div`
  position: sticky;
  top: 96px;
  left: 0;
  height: auto;
`;
